import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { ArrowLeft, Download, Loader2, Headphones, Pause } from "lucide-react";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import MonthlyDossier from "../components/report/MonthlyDossier";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37";

export const VerdictButton = ({ dossierId, token, className = "" }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => { const a = ref.current; if (!a) return; if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); } };
  return (
    <>
      <audio ref={ref} src={`${API}/dossiers/${dossierId}/voice?auth=${token}`} preload="none" onEnded={() => setPlaying(false)} />
      <button data-testid={`dossier-verdict-btn-${dossierId}`} onClick={toggle} className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm text-black hover:brightness-110 transition-all ${className}`}
        style={{ background: `linear-gradient(120deg, #F5E3A0, ${GOLD} 50%, #B8912E)`, boxShadow: `0 0 22px ${GOLD}66` }}>
        {playing ? <Pause size={14} /> : <Headphones size={14} />} {playing ? "Pause Verdict" : "Listen to Coach Barker's Verdict"}
      </button>
    </>
  );
};

// Player-facing released dossier — /dossier/:id
export default function Dossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = usePlayerAuth();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const nodeRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/dossiers/${id}`, { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }).then(({ data }) => setDoc(data)).catch((e) => setErr(e?.response?.status === 404 ? "This dossier doesn't exist or hasn't been released yet." : e?.response?.data?.detail || "Couldn't open the dossier right now."));
  }, [id, token]);

  const download = async () => {
    setSaving(true);
    try {
      const dataUrl = await toPng(nodeRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true, backgroundColor: "#0a0a0a" });
      const a = document.createElement("a"); a.href = dataUrl; a.download = `NextO-Dossier-${doc.month}.png`; a.click();
      toast.success("Dossier saved as PNG", { description: "High-res — ready to share." });
    } catch (e) { toast.error("Couldn't render the PNG."); }
    setSaving(false);
  };

  if (!token) return <div className="min-h-screen bg-black text-white flex items-center justify-center"><button onClick={() => navigate("/login")} className="underline">Log in to view your dossier</button></div>;
  return (
    <div data-testid="dossier-page" className="min-h-screen bg-black text-white">
      <div className="sticky top-0 z-20 bg-[#0a0a0a]/90 backdrop-blur border-b px-4 sm:px-6 py-3 flex items-center justify-between gap-3" style={{ borderColor: `${GOLD}33` }}>
        <button data-testid="dossier-back" onClick={() => navigate("/hub")} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white"><ArrowLeft size={14} /> Hub</button>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {doc?.has_voice && <VerdictButton dossierId={doc.id} token={token} />}
          {doc && <button data-testid="dossier-download" onClick={download} disabled={saving} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm border disabled:opacity-60" style={{ borderColor: `${GOLD}66`, color: GOLD }}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download PNG</button>}
        </div>
      </div>
      {err && <div data-testid="dossier-error" className="p-10 text-center text-white/60">{err}</div>}
      {!doc && !err && <div className="p-10 text-center text-white/40 font-mono text-xs uppercase tracking-widest">Opening dossier…</div>}
      {doc && <div ref={nodeRef}><MonthlyDossier r={doc.report} /></div>}
    </div>
  );
}
